import { hasCommandFeature, hasPermission, isAdminUser, COMMAND_FEATURE_PERMS } from '../permissionControl';

/** 是否已进入指挥系统页面能力范围（管理员或具备「指挥系统页面」） */
function canAccessCommandPage() {
  return isAdminUser() || hasPermission('指挥系统页面');
}

/**
 * 指挥功能权限开关包（唯一来源）。
 * - 管理/审批：细粒度 hasCommandFeature
 * - 查看类（orthoView / mapLayerView）：能进指挥页即可，避免 seed 后未授权导致整箱消失
 * - 画板/底图等：仍走 hasCommandFeature（未 seed 时 fail-open）
 */
export function buildDashboardCommandFeatures() {
  const onCommandPage = canAccessCommandPage();
  return {
    fetchTrack: hasCommandFeature(COMMAND_FEATURE_PERMS.FETCH_TRACK),
    viewTrack: hasCommandFeature(COMMAND_FEATURE_PERMS.VIEW_TRACK),
    downloadTrack: hasCommandFeature(COMMAND_FEATURE_PERMS.DOWNLOAD_TRACK),
    exportTrack: hasCommandFeature(COMMAND_FEATURE_PERMS.EXPORT_TRACK),
    playback: hasCommandFeature(COMMAND_FEATURE_PERMS.PLAYBACK),
    realtimeDraw: hasCommandFeature(COMMAND_FEATURE_PERMS.REALTIME_DRAW),
    drawBoard: hasCommandFeature(COMMAND_FEATURE_PERMS.DRAW_BOARD),
    boardPush: hasCommandFeature(COMMAND_FEATURE_PERMS.BOARD_PUSH),
    basemap: hasCommandFeature(COMMAND_FEATURE_PERMS.BASEMAP),
    memberCommand: hasCommandFeature(COMMAND_FEATURE_PERMS.MEMBER_COMMAND),
    orthoManage: hasCommandFeature(COMMAND_FEATURE_PERMS.ORTHO_MANAGE),
    orthoApprove: hasCommandFeature(COMMAND_FEATURE_PERMS.ORTHO_APPROVE),
    orthoView: onCommandPage || hasCommandFeature(COMMAND_FEATURE_PERMS.ORTHO_VIEW),
    mapLayerManage: hasCommandFeature(COMMAND_FEATURE_PERMS.MAP_LAYER_MANAGE),
    mapLayerView: onCommandPage || hasCommandFeature(COMMAND_FEATURE_PERMS.MAP_LAYER_VIEW),
    panoramaManage: hasCommandFeature(COMMAND_FEATURE_PERMS.PANORAMA_MANAGE),
    panoramaView: onCommandPage || hasCommandFeature(COMMAND_FEATURE_PERMS.PANORAMA_VIEW)
  };
}

/** 是否应挂载「地图叠加」工具箱（桌面/移动共用，避免双分支漂移） */
export function shouldMountOrthoToolbox(commandFeatures = {}) {
  return !!(
    commandFeatures.orthoManage
    || commandFeatures.orthoApprove
    || commandFeatures.orthoView
    || commandFeatures.mapLayerManage
    || commandFeatures.mapLayerView
    || commandFeatures.panoramaManage
    || commandFeatures.panoramaView
  );
}

/** OrthoToolbox 权限 props（与布局挂载条件配套） */
export function buildOrthoToolboxPermissionProps(commandFeatures = {}) {
  return {
    canManage: !!commandFeatures.orthoManage,
    canApprove: !!commandFeatures.orthoApprove,
    canViewLayers: !!commandFeatures.orthoView || !!commandFeatures.orthoManage,
    canMapLayerManage: !!commandFeatures.mapLayerManage || !!commandFeatures.orthoManage,
    canMapLayerView: !!commandFeatures.mapLayerView || !!commandFeatures.mapLayerManage || !!commandFeatures.orthoView,
    canPanoramaManage: !!commandFeatures.panoramaManage,
    canPanoramaView: !!commandFeatures.panoramaView || !!commandFeatures.panoramaManage
  };
}

/** 队员树块 props（传给 DashboardWorkspaceLayout） */
export function buildMemberTreeBlockProps(ctx) {
  return {
    orgMap: ctx.orgMap,
    openOrg: ctx.openOrg,
    openGroup: ctx.openGroup,
    openMember: ctx.openMember,
    setOpenOrg: ctx.setOpenOrg,
    setOpenGroup: ctx.setOpenGroup,
    setOpenMember: ctx.setOpenMember,
    setHighlightedMemberIds: ctx.setHighlightedMemberIds,
    highlightedMemberName: ctx.highlightedMemberName,
    setHighlightedMemberName: ctx.setHighlightedMemberName,
    setActiveMemberId: ctx.setActiveMemberId,
    showTracks: ctx.showTracks,
    isMemberTrackVisible: ctx.isMemberTrackVisible,
    streamingMembers: ctx.streamingMembers,
    areAllGroupsExpanded: ctx.areAllGroupsExpanded,
    areAllMembersExpanded: ctx.areAllMembersExpanded,
    expandOnlineInOrg: ctx.expandOnlineInOrg,
    setOrgGroupsExpanded: ctx.setOrgGroupsExpanded,
    expandOnlineInGroup: ctx.expandOnlineInGroup,
    setGroupMembersExpanded: ctx.setGroupMembersExpanded,
    flyToMember: ctx.flyToMember,
    toggleTrack: ctx.toggleTrack,
    openLiveStream: ctx.openLiveStream,
    startRealTimeTrackDrawing: ctx.startRealTimeTrackDrawing,
    copyToClipboard: ctx.copyToClipboard,
    openDownloadTrackModal: ctx.openDownloadTrackModal,
    fetchMemberTrack: ctx.fetchMemberTrack,
    realTimeTrackDrawing: ctx.realTimeTrackDrawing,
    stopTrackFeatures: ctx.stopTrackFeatures,
    hiddenMapMemberIds: ctx.hiddenMapMemberIds,
    onToggleMemberMapHidden: ctx.onToggleMemberMapHidden,
    onlineTimeoutMs: ctx.onlineTimeoutMs,
    offlineTimeoutMs: ctx.offlineTimeoutMs,
    presenceById: ctx.presenceById,
    commandFeatures: ctx.commandFeatures,
    onSendLocationToMember: ctx.onSendLocationToMember,
    onSendMessageToMember: ctx.onSendMessageToMember,
    onMemberIconChange: ctx.onMemberIconChange,
    notesByKey: ctx.notesByKey,
    onSaveMemberNote: ctx.onSaveMemberNote
  };
}

/** 已授权工作区布局 props（传给 DashboardWorkspaceLayout） */
export function buildDashboardLayoutProps(ctx) {
  return {
    isMobile: ctx.isMobile,
    mapContainer: ctx.mapContainer,
    mapRef: ctx.mapRef,
    membersRef: ctx.membersRef,
    members: ctx.members,
    drawingToolsActiveRef: ctx.drawingToolsActiveRef,
    mapLoadingState: ctx.mapLoadingState,
    isEarthAnimationActive: ctx.isEarthAnimationActive,
    showLeftPanel: ctx.showLeftPanel,
    leftPanelVisible: ctx.leftPanelVisible,
    mapStatus: ctx.mapStatus,
    rescueTargets: ctx.rescueTargets,
    rescueTracks: ctx.rescueTracks,
    mapStyles: ctx.mapStyles,
    currentMapStyleId: ctx.currentMapStyleId,
    styleModeAvailability: ctx.styleModeAvailability,
    onlineNotifications: ctx.onlineNotifications,
    activeMemberId: ctx.activeMemberId,
    networkQuality: ctx.networkQuality,
    devicePerformance: ctx.devicePerformance,
    refreshStatus: ctx.refreshStatus,
    lastRefreshTime: ctx.lastRefreshTime,
    sidebarRef: ctx.sidebarRef,
    collapsed: ctx.collapsed,
    sidebarWidth: ctx.sidebarWidth,
    sidebarMotionEnabled: ctx.sidebarMotionEnabled !== false,
    startTime: ctx.startTime,
    endTime: ctx.endTime,
    showMemberNames: ctx.showMemberNames,
    hideInvalid: ctx.hideInvalid,
    showOnlineOnly: ctx.showOnlineOnly,
    filterMembers: ctx.filterMembers,
    missionMode: ctx.missionMode,
    missionMemberIds: ctx.missionMemberIds,
    setMissionMode: ctx.setMissionMode,
    setMissionMemberIds: ctx.setMissionMemberIds,
    memberPanelTab: ctx.memberPanelTab,
    setMemberPanelTab: ctx.setMemberPanelTab,
    missionPickIds: ctx.missionPickIds,
    onMissionPickIdsChange: ctx.onMissionPickIdsChange,
    onJoinMission: ctx.onJoinMission,
    missionGroups: ctx.missionGroups,
    onMissionGroupsChange: ctx.onMissionGroupsChange,
    onRemoveMissionMember: ctx.onRemoveMissionMember,
    onEndMission: ctx.onEndMission,
    missions: ctx.missions,
    activeMissionId: ctx.activeMissionId,
    onSelectMission: ctx.onSelectMission,
    onCreateMission: ctx.onCreateMission,
    onRenameMission: ctx.onRenameMission,
    onCloseMission: ctx.onCloseMission,
    onToggleMissionRecordTracks: ctx.onToggleMissionRecordTracks,
    missionBadgeCount: ctx.missionBadgeCount,
    isMissionMemberOnline: ctx.isMissionMemberOnline,
    getMissionMemberPresence: ctx.getMissionMemberPresence,
    onFlyToMissionMember: ctx.onFlyToMissionMember,
    voicePulseIds: ctx.voicePulseIds,
    notesByKey: ctx.notesByKey,
    onSaveMemberNote: ctx.onSaveMemberNote,
    onSendLocationToMember: ctx.onSendLocationToMember,
    onSendMessageToMember: ctx.onSendMessageToMember,
    streamingMembers: ctx.streamingMembers,
    openLiveStream: ctx.openLiveStream,
    getMessagesForMember: ctx.getMessagesForMember,
    onMissionChatOutbound: ctx.onMissionChatOutbound,
    onMissionChatOpenChange: ctx.onMissionChatOpenChange,
    playbackTargetNames: ctx.playbackTargetNames,
    setPlaybackTargetNames: ctx.setPlaybackTargetNames,
    playbackSpeed: ctx.playbackSpeed,
    setPlaybackSpeed: ctx.setPlaybackSpeed,
    trackData: ctx.trackData,
    trackColors: ctx.trackColors,
    showTracks: ctx.showTracks,
    toggleTrack: ctx.toggleTrack,
    getMap: ctx.getMap,
    onMissionEmptyHint: ctx.onMissionEmptyHint,
    filteredCount: ctx.filteredCount,
    totalMemberCount: ctx.totalMemberCount,
    onlineMemberCount: ctx.onlineMemberCount,
    memberSearchQuery: ctx.memberSearchQuery,
    onMemberSearchQueryChange: ctx.onMemberSearchQueryChange,
    refreshInterval: ctx.refreshInterval,
    trackModeAndPlaybackUI: ctx.trackModeAndPlaybackUI,
    playbackPanelUI: ctx.playbackPanelUI,
    memberTreeBlockProps: ctx.memberTreeBlockProps,
    commandFeatures: ctx.commandFeatures,
    mobileContainerRef: ctx.mobileContainerRef,
    mobileContainerHeight: ctx.mobileContainerHeight,
    isDragging: ctx.isDragging,
    allTracksVisible: ctx.allTracksVisible,
    showDownload: ctx.showDownload,
    announcement: ctx.announcement,
    showAnnouncement: ctx.showAnnouncement,
    fieldMessageBanners: ctx.fieldMessageBanners,
    maxVisibleFieldBanners: ctx.maxVisibleFieldBanners,
    onCloseFieldMessageBanner: ctx.onCloseFieldMessageBanner,
    onFlyToFieldMessage: ctx.onFlyToFieldMessage,
    trackDownloadModal: ctx.trackDownloadModal,
    showLiveStream: ctx.showLiveStream,
    currentStreamMember: ctx.currentStreamMember,
    stopEarthAnimation: ctx.stopEarthAnimation,
    currentBasemapId: ctx.currentBasemapId,
    activeOverlayIds: ctx.activeOverlayIds,
    loadRescueData: ctx.loadRescueData,
    clearRescueData: ctx.clearRescueData,
    toggleLeftPanel: ctx.toggleLeftPanel,
    switchMapStyle: ctx.switchMapStyle,
    showRescueTargetsList: ctx.showRescueTargetsList,
    showRescueTracksList: ctx.showRescueTracksList,
    renderBasemapLayerControl: ctx.renderBasemapLayerControl,
    locateCommander: ctx.locateCommander,
    fetchDevices: ctx.fetchDevices,
    onSidebarExpand: ctx.onSidebarExpand,
    handleDragMouseDown: ctx.handleDragMouseDown,
    handleResetDashboardConfig: ctx.handleResetDashboardConfig,
    handleReplayEarth: ctx.handleReplayEarth,
    setShowMemberNames: ctx.setShowMemberNames,
    handleShowOnlineTracks: ctx.handleShowOnlineTracks,
    handleDrawOnlineTracks: ctx.handleDrawOnlineTracks,
    setHideInvalid: ctx.setHideInvalid,
    setShowOnlineOnly: ctx.setShowOnlineOnly,
    onExportKml: ctx.onExportKml,
    setStartTime: ctx.setStartTime,
    setEndTime: ctx.setEndTime,
    handleRefreshIntervalChange: ctx.handleRefreshIntervalChange,
    handleMobileDragStart: ctx.handleMobileDragStart,
    handleExpandClick: ctx.handleExpandClick,
    handleCollapseClick: ctx.handleCollapseClick,
    toggleAllTracks: ctx.toggleAllTracks,
    closeAnnouncement: ctx.closeAnnouncement,
    setTrackDownloadModal: ctx.setTrackDownloadModal,
    confirmDownloadMemberTrack: ctx.confirmDownloadMemberTrack,
    closeLiveStream: ctx.closeLiveStream,
    onOnlineNotificationClick: ctx.onOnlineNotificationClick
  };
}
